/**
 * Rôles et acteurs du domaine.
 *
 * Modèle de comptes : cf. ADR-0007. Le parent est le compte titulaire ;
 * l'élève est un sous-profil rattaché, sans PII propre (ni e-mail ni téléphone).
 */
/** Rôle applicatif utilisé par le RBAC. */
export const USER_ROLES = ['eleve', 'parent', 'tuteur', 'admin'];
/**
 * Rôles matérialisés par une ligne `User` côté base (e-mail + mot de passe).
 *
 * Le sous-profil `Eleve` géré par un parent n'en fait pas partie. Exception
 * (SLX-063) : l'élève **autonome** de 14 ans ou plus est un compte titulaire à
 * part entière (e-mail + argon2id + vérification d'adresse) — il apparaît donc
 * ici avec le rôle `eleve`, tout en restant rattaché à un `Eleve` (lui-même).
 */
export const ACCOUNT_ROLES = ['parent', 'tuteur', 'admin', 'eleve'];
/** Moyen de connexion d'un sous-profil élève (géré par le parent). */
export const LEARNER_LOGIN_METHODS = ['identifiant_gere', 'nip_familial'];
/** Matières couvertes par le MVP. */
export const MATIERES = ['francais', 'mathematiques'];
/** Niveaux du secondaire québécois couverts par le MVP. */
export const NIVEAUX_SCOLAIRES = ['sec1', 'sec2', 'sec3', 'sec4', 'sec5'];
/**
 * Séquences de mathématiques de 4e et 5e secondaire (décision du fondateur,
 * 11 septembre 2026) : culture, société et technique ; technico-sciences ;
 * sciences naturelles. Silex couvre les trois.
 */
export const SEQUENCES_MATHS = ['CST', 'TS', 'SN'];
/** Les niveaux où la séquence de maths existe. En dessous, elle n'a pas de sens. */
export const NIVEAUX_AVEC_SEQUENCE_MATHS = ['sec4', 'sec5'];
/** La séquence de maths existe-t-elle à ce niveau ? */
export function sequenceMathsAdmise(niveau) {
    return NIVEAUX_AVEC_SEQUENCE_MATHS.includes(niveau);
}
/**
 * La séquence de maths doit-elle être choisie ? Oui en 4e et 5e secondaire,
 * quand les mathématiques sont actives. Même règle pour le serveur et les
 * deux clients.
 */
export function sequenceMathsExigee(niveau, matieres) {
    return sequenceMathsAdmise(niveau) && matieres.includes('mathematiques');
}
/**
 * LA règle de sélection d'un savoir selon la séquence de l'élève — une seule
 * fonction, appelée partout où un savoir est choisi pour un élève.
 *
 * Vrai quand :
 *  - le savoir ne porte aucune séquence (vide ou absent = commun à toutes :
 *    1re à 3e, français, et tout savoir que le référentiel n'a pas encore
 *    marqué) ;
 *  - l'élève n'a pas de séquence (1re à 3e, ou donnée à compléter : tant
 *    qu'elle manque, rien n'est filtré) ;
 *  - la séquence de l'élève figure dans celles du savoir.
 */
/* ── L'étiquette de séquence et « voir comme » (F-84, 11 octobre 2026) ───────── */
/** Le nom entier de chaque séquence — lu par un lecteur d'écran, jamais abrégé. */
export const NOM_SEQUENCE_MATHS = {
    CST: 'Culture, société et technique',
    TS: 'Technico-sciences',
    SN: 'Sciences naturelles',
};
/** Dans l'ordre CST, TS, SN, sans doublon ni valeur inconnue. */
export function sequencesOrdonnees(sequences) {
    return SEQUENCES_MATHS.filter((s) => sequences.includes(s));
}
/** L'étiquette courte d'une notion réservée : « SN », « CST · TS ». */
export function etiquetteSequences(sequences) {
    return sequencesOrdonnees(sequences).join(' · ');
}
/** Son texte accessible : « Réservé à la séquence Sciences naturelles ». */
export function etiquetteSequencesAccessible(sequences) {
    const noms = sequencesOrdonnees(sequences).map((s) => NOM_SEQUENCE_MATHS[s]);
    if (noms.length === 0)
        return '';
    return noms.length === 1
        ? `Réservé à la séquence ${noms[0]}`
        : `Réservé aux séquences ${noms.slice(0, -1).join(', ')} et ${noms[noms.length - 1]}`;
}
/**
 * « VOIR COMME » — l'en-tête par lequel un écran demande à lire le manuel comme
 * un élève d'une séquence, ou de toutes. Le SERVEUR décide s'il l'honore
 * (`sequenceRegardee`, `modules/manuel/visibilite.ts`) : jamais pour un élève
 * dont la séquence est réglée, ni pour son parent. Le choix ne s'écrit nulle
 * part : il vit le temps de la session.
 */
export const EN_TETE_VOIR_COMME = 'x-silex-voir-comme';
export const VOIR_COMME = ['toutes', ...SEQUENCES_MATHS];
export const LIBELLE_VOIR_COMME = {
    toutes: 'Toutes',
    CST: 'CST',
    TS: 'TS',
    SN: 'SN',
};
export function savoirConvientA(sequencesDuSavoir, sequenceEleve) {
    if (!sequencesDuSavoir || sequencesDuSavoir.length === 0)
        return true;
    if (sequenceEleve === null || sequenceEleve === undefined)
        return true;
    return sequencesDuSavoir.includes(sequenceEleve);
}
//# sourceMappingURL=roles.js.map